/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { FetchData } from 'app/shared/generalMethods';
import { StaffService } from 'app/services/staff.service';

@Component({
  selector: 'app-staff-detail',
  templateUrl: './staff-detail.component.html',
  styleUrls: ['./staff-detail.component.scss']
})
export class StaffDetailComponent implements OnInit {
  isEditMode = false;
  pageLoader = false;
  pageLoaderText = 'Loading...';
  staffForm: FormGroup;
  staffData: any;
  roleOptions: {key: string; value: string}[] = [];
  staffId: string | null = null;
  filteredPrograms: any[] = [];
  countAdmin: number = 0;
  permissionColumns: string[] = ['feature', 'view', 'create', 'edit'];
  staffPermissions: any[] = [];

  constructor(
    private _formBuilder: FormBuilder,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _fetchData: FetchData,
    private _staffService: StaffService,
    private _route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    const staffId = this._route.snapshot.paramMap.get('Id');
    this.loadStaffData(staffId);
  }

  initializeForm(): void {
    this.staffForm = this._formBuilder.group({
      address: ['', Validators.required],
      email: [{ value: '', disabled: true }, [Validators.required, Validators.email]],
      cellPhone: ['', Validators.required],
      role: ['', Validators.required],
      name: ['', Validators.required],
      isActive: ['', Validators.required],
      permissions: this._formBuilder.array([])
    });
  }

  loadStaffData(staffId: string | null): void {
    this.countAdmin = this._fetchData.data?.countAdmin || 0;
    const rolePicklist = this._fetchData.data?.lstRolePicklist;
    this.pageLoader = true;
    const staffList = this._fetchData.data?.lstStaffData;

    if (!staffId || !Array.isArray(staffList)) {
        console.warn('Staff ID not found or staff list is empty.');
        this.pageLoader = false;
        return;
    }

    const matchedStaff = staffList.find((staff: any) => staff.Id === staffId);
    if (!matchedStaff) {
        console.warn(`No staff found with ID: ${staffId}`);
        this.pageLoader = false;
        return;
    }

    this.staffId = staffId;
    this.staffData = matchedStaff;
    
    // Parse permissions - handle HTML-encoded JSON
    if (this.staffData.Permission_Sets__c) {
        try {
            // First decode HTML entities
            const decodedPermissions = this.staffData.Permission_Sets__c
                .replace(/&quot;/g, '"')
                .replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>');

            const permissions = JSON.parse(decodedPermissions);

            this.staffPermissions = [
                { 
                    feature: 'Programs', 
                    view: permissions.programs?.view || false, 
                    create: permissions.programs?.create || false, 
                    edit: permissions.programs?.edit || false,
                    disableCreateEdit: true  
                },
                { 
                    feature: 'Attendance', 
                    view: permissions.attendance?.view || false, 
                    create: permissions.attendance?.create || false, 
                    edit: permissions.attendance?.edit || false 
                },
                { 
                    feature: 'Dashboard', 
                    view: permissions.dashboard?.view || false, 
                    create: false, 
                    edit: false,
                    disableCreateEdit: true 
                },
                { 
                    feature: 'Reports', 
                    view: permissions.reports?.view || false, 
                    create: false, 
                    edit: false,
                    disableCreateEdit: true 
                }
            ];
        } catch (e) {
            this.staffPermissions = this.getDefaultPermissions();
        }
    } else {
        this.staffPermissions = this.getDefaultPermissions();
    }

    this.staffForm.patchValue({
        address: matchedStaff.Staff_Address__c,
        email: matchedStaff.Staff_Email__c,
        cellPhone: matchedStaff.Staff_Phone__c,
        role: matchedStaff.Role__c,
        name: matchedStaff.Staff_Name__c,
        isActive: matchedStaff.Is_Active__c
    });

    this.initializePermissionControls();

    this.roleOptions = Array.isArray(rolePicklist) && rolePicklist.length > 0
        ? rolePicklist
        : [
            { key: 'Admin', value: 'Administrator' },
            { key: 'Staff', value: 'Staff' }
        ];

    this.pageLoader = false;
}

  getDefaultPermissions(): any[] {
    return [
      { feature: 'Programs', view: false, create: false, edit: false,disableCreateEdit: true },
      { feature: 'Attendance', view: false, create: false, edit: false },
      { feature: 'Dashboard', view: false, create: false, edit: false, disableCreateEdit: true },
      { feature: 'Reports', view: false, create: false, edit: false, disableCreateEdit: true }
    ];
  }

  convertPermissionsToArray(permissions: any): any[] {
    return [
      { 
        feature: 'Programs', 
        view: permissions.programs?.view || false, 
        create: permissions.programs?.create || false, 
        edit: permissions.programs?.edit || false,
        disableCreateEdit: true  
      },
      { 
        feature: 'Attendance', 
        view: permissions.attendance?.view || false, 
        create: permissions.attendance?.create || false, 
        edit: permissions.attendance?.edit || false 
      },
      { 
        feature: 'Dashboard', 
        view: permissions.dashboard?.view || false, 
        create: false, 
        edit: false,
        disableCreateEdit: true 
      },
      { 
        feature: 'Reports', 
        view: permissions.reports?.view || false, 
        create: false, 
        edit: false,
        disableCreateEdit: true 
      }
    ];
  }

  initializePermissionControls(): void {
    const permissionArray = this.staffForm.get('permissions') as FormArray;
    permissionArray.clear();

    this.staffPermissions.forEach(permission => {
        const group = this._formBuilder.group({
            feature: [permission.feature],
            view: [permission.view],
            create: [{ value: permission.create, disabled: permission.disableCreateEdit }],
            edit: [{ value: permission.edit, disabled: permission.disableCreateEdit }]
        });
        permissionArray.push(group);
    });
}

  get permissionsArray(): FormArray {
    return this.staffForm.get('permissions') as FormArray;
  }

  get permissionsFormGroups() {
    return this.permissionsArray.controls as FormGroup[];
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;

    if (this.isEditMode) {
      this.staffForm.get('role').enable();
    } else {
      this.staffForm.patchValue({
        address: this.staffData.Staff_Address__c,
        email: this.staffData.Staff_Email__c,
        cellPhone: this.staffData.Staff_Phone__c,
        role: this.staffData.Role__c,
        name: this.staffData.Staff_Name__c,
        isActive: this.staffData.Is_Active__c
      });
      this.staffForm.get('role').disable();
    }
  }

  onFormSubmit(): void {

  if (this.staffForm.invalid) {
    console.warn('Form is invalid');
    this._snackBar.open('Please fill all required fields correctly', 'Close', { duration: 3000 });
    return;
  }

  this.pageLoader = true;
  this.pageLoaderText = 'Updating staff profile...';

  const adminId = localStorage.getItem('adminId');
  if (!adminId) {
    console.error('Admin ID not found in localStorage');
    this._snackBar.open('Admin ID not found', 'Close', { duration: 3000 });
    this.pageLoader = false;
    return;
  }

  const formValues = this.staffForm.getRawValue();

  // Prepare permissions
  const permissions = {};
  this.staffForm.get('permissions').value.forEach(permission => {
    permissions[permission.feature.toLowerCase()] = {
      view: permission.view,
      create: permission.create || false,
      edit: permission.edit || false
    };
  });


  const updateStaffData = {
    Name: this.staffId,
    Administrator_Name__c: adminId,
    Staff_Address__c: formValues.address,
    Staff_Email__c: formValues.email,
    Staff_Phone__c: formValues.cellPhone,
    Role__c: formValues.role,
    Staff_Name__c: formValues.name,
    Is_Active__c: formValues.isActive,
    permissions: permissions
  };


  this.staffPermissions = this.convertPermissionsToArray(permissions);

  this._staffService.updateStaffProfile(adminId, updateStaffData, this.staffId).subscribe(
    (response) => {
      this.pageLoader = false;

      try {
        // If response is a string, parse it
        const parsed = typeof response === 'string' ? JSON.parse(response) : response;

        if (parsed.success) {
          this._snackBar.open(parsed.message || 'Update successful', 'Close', { duration: 3000 });
          this._fetchData.setLocalStorage(response.msg);
          this.loadStaffData(this.staffId);
          this._router.navigate(['/profile-detail']);
        } else {
          console.warn('Update failed:', parsed.message);
          this._snackBar.open(parsed.message || 'Update failed', 'Close');
        }
      } catch (e) {
        console.error('Response parsing failed:', e, 'Original response:', response);
        this._snackBar.open('Failed to process server response', 'Close');
      }
    },
    (error) => {
      console.error('HTTP error:', error);
      this.pageLoader = false;
      this._snackBar.open('Server error occurred', 'Close');
    }
  );
}

  onInputAlphabet(event: any): void {
    const pattern = /^[A-Za-z\s]*$/;
    const input = event.target.value;
    if (!pattern.test(input)) {
      event.target.value = input.replace(/[^A-Za-z\s]/g, '');
      this.staffForm.get(event.target.getAttribute('formControlName'))?.setValue(event.target.value);
    }
  }

  onInputNumeric(event: any): void {
    const pattern = /^[0-9]*$/;
    const input = event.target.value;
    if (!pattern.test(input)) {
      event.target.value = input.replace(/[^0-9]/g, '');
      this.staffForm.get(event.target.getAttribute('formControlName'))?.setValue(event.target.value);
    }
  }
  onInputAlphanumeric(event: any): void {
    const pattern = /^[a-zA-Z0-9 _-]*$/;
    const input = event.target.value;
    if (!pattern.test(input)) {
      event.target.value = input.replace(/[^a-zA-Z0-9/ _-]/g, '');
      const formControlName = event.target.getAttribute('formControlName');
      if (formControlName) {
        this.staffForm.get(formControlName)?.setValue(event.target.value);
      }
    }
}

  // Helper method to get role display value
  getRoleDisplayValue(roleKey: string): string {
    const role = this.roleOptions.find(r => r.key === roleKey);
    return role ? role.value : roleKey;
  }
}

